/*
 * CSV -> cart importer for the B2B cart drawer.
 *
 * DK-PERF-02 / DK-PERF-04 / DK-PERF-11: this was a 55 KB inline <script> inside
 * snippets/cart-drawer.liquid. The cart drawer is rendered by layout/theme.liquid
 * on every page, so those 55 KB were parsed inline (blocking the parser) and
 * re-downloaded with every HTML document instead of being cached once. It
 * contains no Liquid, so it moves out verbatim and is now loaded with defer.
 * Execution order is preserved: deferred scripts run in document order, and
 * dk-cart-queue.js is still loaded ahead of this one from <head>.
 *
 * The code below is unchanged apart from being de-indented by two spaces.
 */
(function () {
  if (window.__dkCsvCartImporterInit) return;
  window.__dkCsvCartImporterInit = true;

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  // v7 — the template fetch used to send `Accept: application/json`, which
  // some B2B Shopify setups route through a JSON-API layer that 404s on
  // theme views, forcing the products.json fallback (no metafields). The
  // header was removed; bumping invalidates any cache populated by the
  // fallback path so the next load uses the template and brings metafields.
  // v9 — the catalog payload changed shape: `suggestedRetailPrice` (deleted
  // metafield) became `wholesaleMsrp` (custom.wholesale_msrp, variant first
  // then product), wholesaleBagSize gained the same variant fallback, and
  // patterns are no longer emitted. v9 also fixes wholesaleMsrp arriving in
  // cents (1500 instead of 15.00). Bumping the key drops every cached copy so
  // nobody downloads a CSV built from an older shape.
  var CATALOG_CACHE_KEY = 'dk:productCatalog:v9';
  var CATALOG_TTL_MS = 15 * 60 * 1000;

  function setStatus(container, html) {
    var status = container.querySelector('[data-csv-status]');
    if (!status) return;
    status.innerHTML = html || '';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#039;');
  }

  function normalizeKey(k){
    return String(k || '').trim().toLowerCase().replace(/\s+/g,'_');
  }

  function normalizeUpc(raw) {
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return '';
    // Unwrap Excel's text-force format. After CSV parsing the surrounding
    // quotes of ="12345" may already be stripped, leaving =12345, so strip
    // both the leading = and any residual quotes.
    if (s.charAt(0) === '=') s = s.slice(1).trim();
    var quoted = s.match(/^"(.+)"$/);
    if (quoted) s = quoted[1].trim();
    if (s.charAt(0) === "'") s = s.slice(1).trim();
    // Excel truncates long numbers to scientific notation (e.g. 4.72609E+13).
    // The real digits are lost — surface a recovery message.
    if (/^-?\d+(\.\d+)?[eE][+\-]?\d+$/.test(s)) return { truncated: true, raw: s };
    // Keep alphanumerics and dashes (some barcodes are alphanumeric). Strip
    // stray whitespace, commas, quotes.
    return s.replace(/[\s,'"]/g, '');
  }

  function parseCSV(text) {
    var rows = [];
    var i = 0, field = '', row = [], inQuotes = false;
    function pushField() { row.push(field); field = ''; }
    function pushRow() { rows.push(row); row = []; }

    while (i < text.length) {
      var c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        }
        field += c; i++; continue;
      }
      if (c === '"') { inQuotes = true; i++; continue; }
      if (c === ',') { pushField(); i++; continue; }
      if (c === '\r') { i++; continue; }
      if (c === '\n') { pushField(); pushRow(); i++; continue; }
      field += c; i++;
    }

    pushField();
    pushRow();

    rows = rows.filter(function (r) {
      return r.some(function (cell) { return String(cell || '').trim() !== ''; });
    });

    if (!rows.length) return { headers: [], data: [] };

    var headers = rows[0].map(normalizeKey);
    var data = rows.slice(1).map(function (r) {
      var obj = {};
      headers.forEach(function (h, idx) { obj[h] = (r[idx] == null ? '' : r[idx]).toString().trim(); });
      return obj;
    });

    return { headers: headers, data: data };
  }

  async function fetchJsonOrText(url, opts) {
    try {
      var res = await fetch(url, opts || {});
      var text = await res.text();
      var json = null;
      try { json = JSON.parse(text); } catch(e){}
      return { ok: res.ok, status: res.status, json: json, text: text };
    } catch (e) {
      // Never throw: a transient network error on one request must not abort the
      // whole CSV run (that would skip the post-add reload). Surface as ok:false.
      return { ok: false, status: 0, json: null, text: (e && e.message) ? e.message : 'Network error' };
    }
  }

  // Every read (catalog pages, barcode enrichment, section renders) goes
  // through the shared queue so the whole page throttles as one and a
  // Cloudflare 1015 pauses reads and writes together. dkCartQueue loads before
  // this snippet (theme.liquid); the direct-fetch fallback keeps the importer
  // working if that asset is ever missing.
  function qRead(url, opts) {
    if (window.dkCartQueue && typeof window.dkCartQueue.read === 'function') {
      return window.dkCartQueue.read(url, opts);
    }
    return fetchJsonOrText(url, opts);
  }

  // ─────────────────────────────────────────────────────────────
  // Product catalog fetch (paginated, parallel, cached)
  // ─────────────────────────────────────────────────────────────
  var _catalogPromise = null;

  function readCatalogCache() {
    try {
      var cached = sessionStorage.getItem(CATALOG_CACHE_KEY);
      if (!cached) return null;
      var parsed = JSON.parse(cached);
      if (!parsed || !parsed.ts || !Array.isArray(parsed.products) || !parsed.products.length) return null;
      if ((Date.now() - parsed.ts) > CATALOG_TTL_MS) return null;
      return parsed.products;
    } catch(e){ return null; }
  }

  function writeCatalogCache(products) {
    try {
      sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ ts: Date.now(), products: products }));
    } catch(e){}
  }

  // Patterns are not wholesale SKUs and are filtered out of the catalog. The
  // template view does this server-side (snippets/is-pattern-product.liquid);
  // this mirrors it on the /products.json fallback, which exposes product_type
  // but no metafields.
  function isPatternProduct(p) {
    return /pattern/i.test(String((p && p.product_type) || ''));
  }

  // Flatten a /products.json product into one row per variant.
  // /products.json doesn't expose `barcode` or metafields, so upc,
  // wholesaleMsrp and wholesaleBagSize will be empty in this
  // fallback — the template view is the preferred source when available.
  function flattenProduct(p) {
    var out = [];
    (p.variants || []).forEach(function(v){
      if (!v || !v.id) return;
      var bc = v.barcode ? String(v.barcode).trim() : '';
      var variantSuffix = (v.title && v.title !== 'Default Title') ? (' - ' + v.title) : '';
      out.push({
        variantId: v.id,
        upc: bc,
        sku: v.sku || '',
        handle: p.handle || '',
        title: (p.title || '') + variantSuffix,
        price: v.price != null ? String(v.price) : '',
        wholesaleMsrp: '',
        wholesaleBagSize: '',
        available: v.available !== false,
        // /products.json doesn't expose inventory levels, so we can only carry
        // the boolean `available`. The template view supplies the richer
        // fields below; here they stay unknown (null) so analyzeCsv trusts
        // `available` alone on this path.
        inventoryQuantity: null,
        inventoryPolicy: '',
        inventoryTracked: null
      });
    });
    return out;
  }

  // /products.json doesn't expose `barcode`, but /products/<handle>.js does.
  // Enrich the given list of products by fetching each unique handle and
  // copying the barcode back onto the matching variant entries.
  async function enrichBarcodesFromHandles(products, opts) {
    var concurrency = (opts && opts.concurrency) || 8;
    var onProgress = opts && opts.onProgress;

    var byHandle = Object.create(null);
    products.forEach(function(p){
      if (!p || !p.handle) return;
      if (!byHandle[p.handle]) byHandle[p.handle] = [];
      byHandle[p.handle].push(p);
    });

    var handles = Object.keys(byHandle);
    if (!handles.length) return products;

    var idx = 0;
    var done = 0;
    async function worker() {
      while (idx < handles.length) {
        var i = idx++;
        var handle = handles[i];
        try {
          var r = await qRead(root + 'products/' + encodeURIComponent(handle) + '.js', {
            headers: { 'Accept': 'application/json' }
          });
          if (r.ok && r.json && Array.isArray(r.json.variants)) {
            var bcByVariant = Object.create(null);
            r.json.variants.forEach(function(v){
              if (v && v.id && v.barcode) bcByVariant[String(v.id)] = String(v.barcode).trim();
            });
            byHandle[handle].forEach(function(p){
              var bc = bcByVariant[String(p.variantId)];
              if (bc && !p.upc) p.upc = bc;
            });
          }
        } catch(_){}
        done++;
        if (onProgress) onProgress(done, handles.length);
      }
    }

    var workers = [];
    for (var w = 0; w < concurrency; w++) workers.push(worker());
    await Promise.all(workers);

    return products;
  }

  // Fetch via the theme's custom template (fast, minimal payload).
  // Throws if the template hasn't been uploaded — caller falls back.
  //
  // IMPORTANT: do NOT send `Accept: application/json` on these requests.
  // Some Shopify B2B configurations route JSON-accept requests through a
  // separate API layer that does not recognize theme `?view=` templates
  // and returns 404. The template renders JSON content, but it must be
  // reached via the normal HTML route. The body is still parsed as JSON
  // by fetchJsonOrText.
  async function fetchCatalogViaTemplate(bust) {
    var base = root + 'collections/all?view=product-catalog';
    // On a hard refresh, `bust` is a unique token appended to every request so
    // the response skips the browser HTTP cache and Shopify's edge cache — not
    // just the theme's own sessionStorage copy.
    var suffix = bust ? ('&_cb=' + bust) : '';
    var first = await qRead(base + '&page=1' + suffix);
    if (!first.ok || !first.json || !Array.isArray(first.json.products)) {
      throw new Error('template-unavailable');
    }
    var totalPages = first.json.total_pages || 1;
    var all = first.json.products.slice();
    if (totalPages > 1) {
      // The queue caps these at 2-in-flight; enqueueing them all at once no
      // longer fires N simultaneous requests (that burst was a 1015 trigger).
      var reqs = [];
      for (var p = 2; p <= totalPages; p++) {
        reqs.push(qRead(base + '&page=' + p + suffix));
      }
      var results = await Promise.all(reqs);
      results.forEach(function(r){
        if (r.ok && r.json && Array.isArray(r.json.products)) all = all.concat(r.json.products);
      });
    }
    return all;
  }

  // Fallback: native Shopify /products.json (always available).
  // Paginates sequentially at 250/page, stops on an empty page.
  async function fetchCatalogViaProductsJson(bust) {
    var all = [];
    var page = 1;
    var MAX_PAGES = 50;
    var suffix = bust ? ('&_cb=' + bust) : '';
    while (page <= MAX_PAGES) {
      var r = await qRead(root + 'products.json?limit=250&page=' + page + suffix, {
        headers: { 'Accept': 'application/json' }
      });
      if (!r.ok || !r.json || !Array.isArray(r.json.products)) {
        if (page === 1) throw new Error('Could not load products (HTTP ' + r.status + ')');
        break;
      }
      var batch = r.json.products;
      if (!batch.length) break;
      batch.forEach(function(p){
        if (isPatternProduct(p)) return;
        flattenProduct(p).forEach(function(v){ all.push(v); });
      });
      if (batch.length < 250) break;
      page++;
    }
    return all;
  }

  function fetchCatalog(forceReload) {
    if (!forceReload && _catalogPromise) return _catalogPromise;
    if (!forceReload) {
      var cached = readCatalogCache();
      if (cached) {
        _catalogPromise = Promise.resolve(cached);
        return _catalogPromise;
      }
    }

    // A hard refresh threads a unique cache-buster through both fetch paths so
    // the request bypasses every network cache; a normal load leaves it blank.
    var bust = forceReload ? String(Date.now()) : '';

    _catalogPromise = (async function(){
      var products;
      var source = 'template';
      try {
        products = await fetchCatalogViaTemplate(bust);
      } catch(e) {
        source = 'products.json';
        console.warn('[dk:csv] product-catalog template unavailable, falling back to /products.json — UPCs will be empty until the template is deployed.', e);
        products = await fetchCatalogViaProductsJson(bust);
      }
      try {
        var withUpc = (products || []).filter(function(p){ return p && p.upc; }).length;
        console.info('[dk:csv] catalog loaded via ' + source + ' — ' + (products || []).length + ' variants (' + withUpc + ' with UPC)');
      } catch(_){}
      if (products && products.length) writeCatalogCache(products);
      return products || [];
    })();

    _catalogPromise.catch(function(){ _catalogPromise = null; });
    return _catalogPromise;
  }

  // Hard refresh, wired to the "Refresh catalog" button. Drops every cached
  // copy of the catalog (in-memory promise + the 15-min sessionStorage cache)
  // and re-pulls fresh from the store with a cache-busting param, so a shopper
  // who just saw the merchant fix/delete a product can pull the change without
  // closing the tab or waiting for the TTL to expire.
  async function refreshCatalog(btn, container) {
    var originalLabel = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Refreshing…';
    setStatus(container, '<div class="csv-status__ok">Fetching the latest catalog from the store…</div>');
    try {
      try { sessionStorage.removeItem(CATALOG_CACHE_KEY); } catch(_){}
      _catalogPromise = null;
      var products = await fetchCatalog(true);
      var count = (products || []).length;
      if (!count) {
        setStatus(container, '<div class="csv-status__warn">The store returned an empty catalog. If you just made a change, wait a moment and try again.</div>');
        return;
      }
      setStatus(container, '<div class="csv-status__ok">&#x2713; Catalog updated &mdash; <strong>' + count + '</strong> item(s) loaded. You can now generate the CSV or upload your file.</div>');
    } catch (e) {
      setStatus(container, '<div class="csv-status__err"><strong>Could not refresh the catalog:</strong> ' + escapeHtml(e && e.message ? e.message : String(e)) + '</div>');
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalLabel;
    }
  }

  function buildUpcIndex(products) {
    var idx = Object.create(null);
    products.forEach(function(p){
      if (!p || !p.upc) return;
      var key = String(p.upc).trim();
      if (key && !idx[key]) idx[key] = p;
    });
    return idx;
  }

  function buildVariantIdIndex(products) {
    var idx = Object.create(null);
    products.forEach(function(p){
      if (!p || !p.variantId) return;
      var key = String(p.variantId).trim();
      if (key && !idx[key]) idx[key] = p;
    });
    return idx;
  }

  // ─────────────────────────────────────────────────────────────
  // Cart add — delegated to the shared queue (dk-cart-queue.js), which
  // serializes /cart/add.js, honors Retry-After on a Cloudflare 1015, and
  // bisects a rejected chunk instead of firing one request per row. The old
  // per-row fallback turned a batch of 100 with one bad variant into ~505
  // requests — the amplification that tripped the 1015 in the first place.
  // ─────────────────────────────────────────────────────────────
  async function addItemsSmartBatch(items, onProgress) {
    if (!items.length) return { added: [], failed: [], rateLimited: false };

    if (window.dkCartQueue && typeof window.dkCartQueue.addBatch === 'function') {
      return window.dkCartQueue.addBatch(items, { chunkSize: 100, onProgress: onProgress });
    }

    // Fallback (queue asset missing): one atomic request per 100 items, no
    // per-row explosion. A failed chunk is reported whole rather than retried.
    var CHUNK = 100, added = [], failed = [], processed = 0;
    for (var start = 0; start < items.length; start += CHUNK) {
      var chunk = items.slice(start, start + CHUNK);
      var r = await fetchJsonOrText(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: chunk.map(function(it){ return { id: it.id, quantity: it.quantity }; }) })
      });
      if (r.ok) {
        added = added.concat(chunk);
      } else {
        var msg = (r.json && (r.json.description || r.json.message)) || ('HTTP ' + r.status);
        chunk.forEach(function(it){ failed.push(Object.assign({}, it, { error: msg })); });
      }
      processed += chunk.length;
      if (onProgress) onProgress(processed, items.length, added.length, failed.length);
    }
    return { added: added, failed: failed, rateLimited: false };
  }

  // ─────────────────────────────────────────────────────────────
  // Cart section re-render
  // ─────────────────────────────────────────────────────────────
  function getSectionHandleFromElement(el) {
    if (!el) return null;
    var section = el.closest('[id^="shopify-section-"]');
    if (!section || !section.id) return null;
    return section.id.replace('shopify-section-','').trim();
  }

  function getCartDrawerHandle() {
    return getSectionHandleFromElement(document.querySelector('#CartDrawer')) || 'cart-drawer';
  }

  function getCartBubbleHandle() {
    return getSectionHandleFromElement(document.querySelector('#cart-icon-bubble')) || 'cart-icon-bubble';
  }

  function replaceSectionHTML(handle, html) {
    if (!handle || !html) return false;
    var current = document.getElementById('shopify-section-' + handle);
    if (!current) return false;
    var temp = document.createElement('div');
    temp.innerHTML = html;
    var incoming = temp.querySelector('#shopify-section-' + handle);
    if (!incoming) { current.innerHTML = html; return true; }
    current.replaceWith(incoming);
    return true;
  }

  async function refreshCartSections() {
    var drawerHandle = getCartDrawerHandle();
    var bubbleHandle = getCartBubbleHandle();
    var handles = [];
    [drawerHandle, bubbleHandle].forEach(function(h){
      if (h && handles.indexOf(h) === -1) handles.push(h);
    });

    var endpoints = [
      root + 'cart?sections=' + encodeURIComponent(handles.join(',')),
      root + '?sections=' + encodeURIComponent(handles.join(','))
    ];

    var payload = null;
    for (var i = 0; i < endpoints.length; i++){
      try {
        var r = await qRead(endpoints[i], { headers: { 'Accept': 'application/json' } });
        if (r.json && typeof r.json === 'object') { payload = r.json; break; }
      } catch(e){}
    }

    if (!payload) {
      try {
        var cartRes = await qRead(root + 'cart.js', { headers: { 'Accept': 'application/json' } });
        var cart = cartRes.json;
        var bubble = document.querySelector('#cart-icon-bubble');
        if (bubble && cart && typeof cart.item_count === 'number') {
          bubble.setAttribute('aria-label', cart.item_count + ' items');
          var countNode = bubble.querySelector('[aria-hidden="true"], .cart-count-bubble span, .count');
          if (countNode) countNode.textContent = String(cart.item_count);
        }
      } catch(e){}
      return;
    }

    var replacedDrawer = false;
    if (payload[drawerHandle]) replacedDrawer = replaceSectionHTML(drawerHandle, payload[drawerHandle]);
    if (payload[bubbleHandle]) replaceSectionHTML(bubbleHandle, payload[bubbleHandle]);

    document.dispatchEvent(new CustomEvent('dk:cart:refreshed'));

    // Only fall back to a full reload if the in-place section swap failed.
    // (Used by the Empty-cart flow; the CSV flow reloads on its own button.)
    if (!replacedDrawer) location.reload();
  }

  // ─────────────────────────────────────────────────────────────
  // CSV parse — treats id_producto as a raw variant_id and sends it
  // straight to /cart/add.js. No catalog lookup required.
  // ─────────────────────────────────────────────────────────────
  function analyzeCsv(text, upcIndex, variantIdIndex) {
    var parsed = parseCSV(text);
    var headers = parsed.headers;

    function pickKey(keys) {
      for (var i = 0; i < keys.length; i++) {
        if (headers.indexOf(keys[i]) >= 0) return keys[i];
      }
      return null;
    }

    // A row can be identified by a UPC/barcode OR a variant id. The Wac PO
    // export uses `upc, item, qty`; the catalog template uses `id_product`.
    var upcKey    = pickKey(['upc','barcode','variant_barcode','product_barcode','ean','gtin']);
    var idKey     = pickKey(['id_producto','idproducto','id_product','idproduct','product_id','productid','variant_id','variantid','id']);
    var qtyKey    = pickKey(['quantity','qty','quant','q','amount','cantidad']);
    var nameKey   = pickKey(['product_name','name_product','nameproduct','name','title','description','producto','item','item_name']);
    var priceKey  = pickKey(['price','variant_price','unit_price','precio']);

    // We can add items as long as there's *some* identifier (UPC or id) plus
    // a quantity column.
    if ((!idKey && !upcKey) || !qtyKey) {
      var looksLikeShopifyExport = headers.indexOf('handle') >= 0 && headers.indexOf('variant_sku') >= 0;
      var missing = [];
      if (!idKey && !upcKey) missing.push('<code>upc</code> or <code>id_product</code>');
      if (!qtyKey) missing.push('<code>qty</code>');

      var msg = 'This CSV is missing the ' + missing.join(' and ') +
                ' column' + (missing.length > 1 ? 's' : '') + ' needed to add items to the cart.';

      if (looksLikeShopifyExport) {
        msg += ' It looks like a raw Shopify product export — please use the template (with <code>upc, item, qty</code> or <code>id_product, name_product, upc, price, qty</code> columns) instead.';
      } else {
        var preview = headers.slice(0, 5).map(function(h){ return '<code>' + escapeHtml(h) + '</code>'; }).join(', ');
        var extra = headers.length > 5 ? ' (+' + (headers.length - 5) + ' more)' : '';
        msg += ' Your file has ' + headers.length + ' column' + (headers.length === 1 ? '' : 's') +
               ': ' + preview + extra + '.';
      }
      return { error: msg };
    }

    var catalogAvailable = !!variantIdIndex || !!upcIndex;

    var rows = parsed.data.map(function (r, idx) {
      return {
        row: idx + 2,
        rawId:    idKey    ? (r[idKey]    == null ? '' : r[idKey]).toString().trim()    : '',
        rawQty:   (r[qtyKey]  == null ? '' : r[qtyKey]).toString().trim(),
        rawName:  nameKey  ? (r[nameKey]  == null ? '' : r[nameKey]).toString().trim()  : '',
        rawUpc:   upcKey   ? (r[upcKey]   == null ? '' : r[upcKey]).toString().trim()   : '',
        rawPrice: priceKey ? (r[priceKey] == null ? '' : r[priceKey]).toString().trim() : ''
      };
    }).filter(function(r){ return r.rawId !== '' || r.rawUpc !== '' || r.rawQty !== ''; });

    if (!rows.length) return { error: 'No rows found in the CSV.' };

    var formatErrors     = [];
    var outOfStock       = []; // rows the catalog says are unavailable
    var items            = [];
    var catalogMissCount = 0; // UPC rows we couldn't match because no catalog loaded

    // Resolve a normalized token to a catalog product — variant id first,
    // then UPC/barcode.
    function lookup(token) {
      if (!token) return null;
      if (variantIdIndex && variantIdIndex[token]) return variantIdIndex[token];
      if (upcIndex && upcIndex[token]) return upcIndex[token];
      return null;
    }

    rows.forEach(function (r) {
      var rowLabel = 'Row ' + r.row + (r.rawName ? ' ("' + escapeHtml(r.rawName) + '")' : '');

      // Quantity. A blank/whitespace cell is an intentional "skip this row" and
      // stays silent. But a cell that was filled in with something that isn't a
      // whole positive number (e.g. "two", "1.5", "3 bags", "-2") is almost
      // certainly a mistake — surface it instead of dropping it silently, so
      // the shopper doesn't think that row was ordered.
      if (r.rawQty === '') return;
      var q;
      if (/^\d+$/.test(r.rawQty)) {
        q = parseInt(r.rawQty, 10);
        if (q <= 0) return; // an explicit 0 means skip, same as blank
      } else {
        formatErrors.push(rowLabel + ': quantity <code>' + escapeHtml(r.rawQty) + '</code> is not a whole number, so the row was skipped. Use a number like <code>3</code>.');
        return;
      }

      // Normalize the id token.
      var idNorm = '';
      if (r.rawId) {
        var nId = normalizeUpc(r.rawId);
        if (nId && typeof nId === 'object' && nId.truncated) {
          formatErrors.push(rowLabel + ': ID <code>' + escapeHtml(r.rawId) + '</code> was truncated by Excel and the real digits are lost. Please re-download the CSV and try again.');
          return;
        }
        idNorm = (nId && typeof nId === 'string') ? nId : '';
      }

      // Normalize the upc token.
      var upcNorm = '';
      if (r.rawUpc) {
        var nUpc = normalizeUpc(r.rawUpc);
        if (nUpc && typeof nUpc === 'object' && nUpc.truncated) {
          formatErrors.push(rowLabel + ': UPC <code>' + escapeHtml(r.rawUpc) + '</code> was truncated by Excel and the real digits are lost. Please re-download the CSV and try again.');
          return;
        }
        upcNorm = (nUpc && typeof nUpc === 'string') ? nUpc : '';
      }

      if (!idNorm && !upcNorm) {
        formatErrors.push(rowLabel + ': no product UPC or ID found.');
        return;
      }

      var product   = lookup(idNorm) || lookup(upcNorm);
      var variantId = null;
      var missing   = false;

      if (product) {
        variantId = product.variantId;

        // Stock pre-check. The catalog already tells us availability, so we can
        // skip a sold-out variant WITHOUT spending a /cart/add.js request that
        // would fail — and, crucially, tell the shopper about it instead of
        // letting them believe every row was ordered.
        //
        // `available` is Shopify's own boolean: false only when the variant is
        // genuinely unpurchasable (tracked, deny policy, zero stock). A variant
        // with an "allow overselling" policy, or with untracked inventory,
        // reports available:true and is left alone. We treat availability as
        // unknown (never blocking) when the field wasn't supplied.
        if (product.available === false) {
          outOfStock.push({
            _row: r.row,
            _idDisplay: String(variantId),
            _title: product.title || '',
            _csvName: r.rawName
          });
          return;
        }
      } else if (idNorm && /^\d+$/.test(idNorm)) {
        // We have a numeric variant id. Even when the catalog index is present
        // but INCOMPLETE (e.g. a catalog page failed to load), trust the id and
        // let /cart/add.js be the final arbiter instead of dropping the row as
        // "not found". This is what prevents valid rows from silently vanishing
        // (the catalog CSV always uses numeric variant ids in id_product).
        variantId = idNorm;
      } else if (upcNorm && !idNorm && !upcIndex) {
        // A UPC was given but there's no UPC index to resolve it.
        catalogMissCount++;
        return;
      } else {
        // Catalog is loaded but neither token matched anything.
        missing   = true;
        variantId = idNorm || upcNorm;
      }

      items.push({
        id: Number(variantId),
        quantity: q,
        _row: r.row,
        _idDisplay: String(variantId),
        _title: product ? product.title : '',
        _csvName: r.rawName,
        _csvUpc: upcNorm || (product ? product.upc : ''),
        _csvPrice: r.rawPrice,
        _missing: missing
      });
    });

    if (catalogMissCount && !items.length) {
      return { error: 'This file matches products by UPC, but the product catalog could not be loaded to look them up. Please try again in a moment.' };
    }

    return {
      items: items,
      formatErrors: formatErrors,
      outOfStock: outOfStock,
      hasUpc: !!upcKey,
      hasPrice: !!priceKey,
      catalogChecked: catalogAvailable
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Results panel — anchored to <body>, OUTSIDE the cart-drawer section, so
  // the drawer's post-add re-render never wipes it. It replaces the old
  // approach of writing the report into the drawer and then reloading the page
  // (which erased it), and stays until the shopper closes it — no timer.
  // ─────────────────────────────────────────────────────────────
  var _lastProblemRows = [];
  var _reloadOnClose = false;
  var _reportReturnFocus = null;

  function closeReportPanel() {
    var el = document.getElementById('dk-csv-report');
    if (el && el.parentNode) el.parentNode.removeChild(el);
    if (_reportReturnFocus && document.contains(_reportReturnFocus)) {
      _reportReturnFocus.focus();
    }
    _reportReturnFocus = null;
  }

  // DK-A11Y-036 (WCAG 2.1.1 / 2.4.3). The panel is appended to <body>, outside
  // the cart drawer, and the drawer's focus trap keeps Tab inside #CartDrawer —
  // so keyboard users could never reach "Continue to cart", "Download" or
  // "Close". Focus now moves into the panel when it opens, Tab cycles through
  // its own buttons, Escape dismisses it like the × button, and focus goes
  // back to whatever launched the import when it closes without a reload.
  function onReportKeydown(e) {
    var panel = e.currentTarget;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      dismissReport();
      return;
    }
    if (e.key !== 'Tab') return;
    var focusables = Array.prototype.filter.call(
      panel.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && el.offsetParent !== null; }
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function problemsTable(rows) {
    var body = rows.map(function(p){
      return '<tr>' +
        '<td>' + escapeHtml(String(p._row == null ? '' : p._row)) + '</td>' +
        '<td>' + escapeHtml(p._title || p._csvName || '') +
          (p._idDisplay ? ' <span style="opacity:.6">(' + escapeHtml(p._idDisplay) + ')</span>' : '') + '</td>' +
        '<td class="dk-csv-report__reason">' + escapeHtml(p._reason || '') + '</td>' +
      '</tr>';
    }).join('');
    // DK-A11Y-037 (WCAG 1.3.1): column headers carry scope and the table is
    // named with aria-label (a visually hidden <caption> still takes height).
    return '<table class="dk-csv-report__table" aria-label="Rows not added to the cart">' +
      '<thead><tr>' +
      '<th scope="col">Row</th><th scope="col">Product</th><th scope="col">Reason</th></tr></thead><tbody>' + body + '</tbody></table>';
  }

  // data = { addedCount, cartCount, problems:[{_row,_title,_csvName,_idDisplay,_reason}],
  //          formatErrors:[html], rateLimited:bool, rateLimitMessage:str }
  function renderReportPanel(data) {
    var returnFocus = _reportReturnFocus || document.activeElement;
    if (returnFocus && returnFocus.classList && returnFocus.classList.contains('csv-file-input')) {
      var uploader = closest(returnFocus, '[data-csv-uploader]');
      returnFocus = (uploader && uploader.querySelector('[data-csv-pick]')) || returnFocus;
    }
    closeReportPanel();
    _reportReturnFocus = returnFocus && returnFocus !== document.body ? returnFocus : null;
    _lastProblemRows = data.problems || [];

    var panel = document.createElement('div');
    panel.className = 'dk-csv-report';
    panel.id = 'dk-csv-report';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'CSV import results');
    panel.setAttribute('tabindex', '-1');
    panel.addEventListener('keydown', onReportKeydown);

    var addedOk = data.addedCount > 0;
    var problemCount = (data.problems ? data.problems.length : 0);

    var html = '';
    html += '<div class="dk-csv-report__head">';
    html +=   '<p class="dk-csv-report__title">Import results</p>';
    html +=   '<button type="button" class="dk-csv-report__close" data-csv-report-close aria-label="Close">&times;</button>';
    html += '</div>';

    html += '<div class="dk-csv-report__body">';
    html +=   '<p class="dk-csv-report__summary">';
    html +=     '<span class="dk-csv-report__stat--ok">&#x2713; ' + data.addedCount + ' added to cart</span>';
    if (problemCount) html += '<span class="dk-csv-report__stat--err">&#9888; ' + problemCount + ' not added</span>';
    html +=   '</p>';

    if (typeof data.cartCount === 'number') {
      html += '<p style="opacity:.75;margin:0 0 4px;">Your cart now holds ' + data.cartCount + ' item(s) in total.</p>';
    }

    if (data.rateLimited) {
      html += '<div class="dk-csv-report__banner">' + escapeHtml(data.rateLimitMessage || '') + '</div>';
    }

    if (problemCount) {
      html += '<div class="dk-csv-report__group">';
      html +=   '<p class="dk-csv-report__group-title">These rows were not added</p>';
      html +=   problemsTable(data.problems);
      html += '</div>';
    }

    if (data.formatErrors && data.formatErrors.length) {
      html += '<div class="dk-csv-report__group">';
      html +=   '<p class="dk-csv-report__group-title">Rows skipped for formatting</p>';
      html +=   '<ul style="margin:0;padding-left:18px;">' +
                  data.formatErrors.map(function(e){ return '<li>' + e + '</li>'; }).join('') + '</ul>';
      html += '</div>';
    }

    if (!problemCount && !(data.formatErrors && data.formatErrors.length) && addedOk) {
      html += '<p class="dk-csv-report__stat--ok" style="margin:8px 0 0;">Every row in your file was added. You can check out now.</p>';
    }

    html += '</div>';

    html += '<div class="dk-csv-report__actions">';
    if (addedOk) {
      // Nothing reloads until one of these is clicked, so the errors above stay
      // put. Both reload; Continue also reopens the cart on the fresh load.
      html += '<button type="button" class="dk-csv-report__btn dk-csv-report__btn--primary" data-csv-report-continue>Continue to cart</button>';
      html += '<button type="button" class="dk-csv-report__btn" data-csv-report-skip>Skip</button>';
    }
    if (problemCount) html += '<button type="button" class="dk-csv-report__btn" data-csv-report-download>Download not-added rows (CSV)</button>';
    if (!addedOk) html += '<button type="button" class="dk-csv-report__btn" data-csv-report-close>Close</button>';
    html += '</div>';

    panel.innerHTML = html;
    document.body.appendChild(panel);
    panel.focus();
  }

  function downloadProblemRows() {
    if (!_lastProblemRows.length) return;
    var header = ['id_product','name_product','qty','reason'].map(csvEscape).join(',');
    var body = _lastProblemRows.map(function(p){
      return [
        excelTextCell(p._idDisplay || ''),
        csvEscape(cleanTitle(p._title || p._csvName || '')),
        csvEscape(p._qty == null ? '' : p._qty),
        csvEscape(p._reason || '')
      ].join(',');
    });
    triggerDownload('csv-import-not-added.csv', [header].concat(body).join('\r\n'));
  }

  // ─────────────────────────────────────────────────────────────
  // Add to cart — sends valid rows through the throttled queue, then shows a
  // single persistent report covering everything that didn't make it in
  // (sold out, not found, rejected by the server, malformed, rate-limited).
  // ─────────────────────────────────────────────────────────────
  async function executeAddToCart(container, buckets) {
    var validItems = buckets.valid;

    var result = { added: [], failed: [], rateLimited: false };
    if (validItems.length) {
      setStatus(container, '<div class="csv-status__ok">Adding ' + validItems.length + ' item(s) to your cart…</div>');
      try {
        result = await addItemsSmartBatch(validItems, function(done, total, added, failed){
          setStatus(container,
            '<div class="csv-status__ok">Processing… ' + done + ' / ' + total +
            ' (' + added + ' added' + (failed ? ', <span style="color:#b00020">' + failed + ' failed</span>' : '') + ')</div>'
          );
        });
      } catch(e) {
        setStatus(container, '<div class="csv-status__err"><strong>Something went wrong:</strong> ' + escapeHtml(e && e.message ? e.message : String(e)) + '</div>');
        return;
      }
    }

    // Assemble every "not added" row into one list, each with a plain reason.
    var problems = [];
    buckets.outOfStock.forEach(function(p){
      problems.push({ _row: p._row, _title: p._title, _csvName: p._csvName, _idDisplay: p._idDisplay, _reason: 'Out of stock' });
    });
    buckets.notFound.forEach(function(m){
      problems.push({ _row: m._row, _title: m._title, _csvName: m._csvName, _idDisplay: m._idDisplay, _qty: m.quantity, _reason: 'Not found in store' });
    });
    result.failed.forEach(function(f){
      problems.push({
        _row: f._row, _title: f._title, _csvName: f._csvName, _idDisplay: f._idDisplay, _qty: f.quantity,
        _reason: f.reason === 'rate_limited' ? 'Not added — store was rate-limiting' : (f.error || 'Rejected by the store')
      });
    });
    problems.sort(function(a, b){ return (a._row || 0) - (b._row || 0); });

    // Read the new cart total for the summary. This is a plain /cart.js read —
    // NOT a page reload and NOT a drawer refresh — so the report the shopper is
    // reading is never disturbed. The visible cart update is deferred to the
    // "Continue" button below, so the errors stay on screen until they choose
    // to move on.
    var cartCount;
    if (result.added.length) {
      try {
        var cr = window.dkCartQueue ? await window.dkCartQueue.getCart() : null;
        if (cr && cr.json && typeof cr.json.item_count === 'number') cartCount = cr.json.item_count;
      } catch(e){}
    }
    setStatus(container, '');

    // No reload happens now — the report stays on screen until the shopper
    // clicks. Only their explicit "Continue to cart" / "Skip" then reloads
    // (see below), so the errors are never wiped before they're read.
    _reloadOnClose = result.added.length > 0;

    renderReportPanel({
      addedCount: result.added.length,
      cartCount: cartCount,
      problems: problems,
      formatErrors: buckets.formatErrors,
      rateLimited: result.rateLimited,
      rateLimitMessage: (window.dkCartQueue && window.dkCartQueue.rateLimitMessage) || ''
    });
  }

  // "Continue to cart" and "Skip" both reload the page — a full reload is the
  // surest way to render the freshly-built cart with correct totals and B2B
  // pricing. The reload only fires on this explicit click, after the shopper
  // has read the report. Continue also flags the drawer to reopen on the new
  // load so they land on their cart; Skip just reloads.
  function continueAfterReport() {
    try { sessionStorage.setItem('dk_open_cart_after_reload', '1'); } catch(e){}
    window.location.reload();
  }

  function skipAfterReport() {
    window.location.reload();
  }

  // Header × / "Close": if items were added, proceed like Skip (reload so the
  // cart renders accurately); otherwise nothing changed — just remove the panel.
  function dismissReport() {
    if (_reloadOnClose) { window.location.reload(); return; }
    closeReportPanel();
  }

  // ─────────────────────────────────────────────────────────────
  // Main file handler — reads the CSV and auto-adds the valid items
  // straight to the cart (no preview / confirmation step).
  // ─────────────────────────────────────────────────────────────
  async function handleFile(container, file) {
    setStatus(container, '<div class="csv-status__ok">Reading file…</div>');

    var text;
    try {
      text = await file.text();
    } catch(e) {
      setStatus(container, '<div class="csv-status__err"><strong>Error:</strong> ' + escapeHtml(e && e.message ? e.message : String(e)) + '</div>');
      return;
    }

    // Load the catalog so we can pre-validate variant IDs and stock against the
    // store. If it fails (template 404, network, etc.) we fall through: rows are
    // still added and the server has the final say on validity.
    var upcIndex = null;
    var variantIdIndex = null;
    try {
      setStatus(container, '<div class="csv-status__ok">Checking catalog…</div>');
      var catalog = await fetchCatalog();
      if (catalog && catalog.length) {
        upcIndex = buildUpcIndex(catalog);
        variantIdIndex = buildVariantIdIndex(catalog);
      }
    } catch(e) {
      upcIndex = null;
      variantIdIndex = null;
    }

    var analysis = analyzeCsv(text, upcIndex, variantIdIndex);

    if (analysis.error) {
      setStatus(container, '<div class="csv-status__err"><strong>CSV error:</strong> ' + analysis.error + '</div>');
      return;
    }

    var items        = analysis.items;
    var missingItems = items.filter(function(it){ return it._missing; });
    var validItems   = items.filter(function(it){ return !it._missing; });

    var buckets = {
      valid: validItems,
      notFound: missingItems,
      outOfStock: analysis.outOfStock || [],
      formatErrors: analysis.formatErrors || []
    };

    // Always hand off to executeAddToCart — even with nothing to send, it
    // shows the report panel (sold out / not found / malformed) so a file that
    // added nothing never looks like a silent success.
    await executeAddToCart(container, buckets);
  }

  // ─────────────────────────────────────────────────────────────
  // CSV download (entire active catalog with UPC)
  // ─────────────────────────────────────────────────────────────
  function csvEscape(v) {
    var s = String(v == null ? '' : v);
    if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g,'""') + '"';
    return s;
  }

  function triggerDownload(filename, csv) {
    var blob = new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(function(){
      if (a.parentNode) a.parentNode.removeChild(a);
      URL.revokeObjectURL(url);
    }, 500);
  }

  // Excel truncates long numbers (UPCs) to scientific notation when opening
  // a plain CSV. Wrapping the value as ="12345" forces Excel to treat the
  // cell as text, and Excel strips the wrapper when the user saves back to
  // CSV — so the round-trip download → edit → upload keeps UPCs intact.
  //
  // The money columns get the same treatment for a different reason: the file
  // already holds "15.00", but a spreadsheet parses it as the number 15 and
  // drops the trailing zero on screen. A CSV carries no cell formatting, so
  // forcing text is the only way to make the .00 survive — requested by the
  // client. Trade-off: those columns are text, so they can't be summed or
  // sorted numerically in the sheet. Nothing reads them on import (only
  // id_product / upc / qty are parsed), so the round-trip is unaffected.
  function excelTextCell(v) {
    var s = String(v == null ? '' : v);
    return '="' + s.replace(/"/g, '""') + '"';
  }

  function cleanTitle(t) {
    return String(t == null ? '' : t).replace(/\s*-\s*Default Title$/i, '').trim();
  }

  function buildCsv(products) {
    var header = ['id_product','name_product','upc','price','wholesale_msrp','wholesale_bag_size','qty'].map(csvEscape).join(',');
    var body = [];
    products.forEach(function(p){
      if (!p || !p.variantId) return;
      body.push([
        excelTextCell(p.variantId),               // id_product — variant ID (Excel-safe)
        csvEscape(cleanTitle(p.title)),           // name_product
        excelTextCell(p.upc || ''),               // upc — Excel-safe so long barcodes stay intact
        excelTextCell(p.price || ''),             // price — text-forced so 166.00 keeps its .00 on screen
        excelTextCell(p.wholesaleMsrp || ''),     // wholesale_msrp (variant metafield, product fallback) — same, so both money columns match
        csvEscape(p.wholesaleBagSize || ''),      // wholesale_bag_size (integer: units per bag, no decimals)
        ''                                        // qty — left empty for the user to fill in
      ].join(','));
    });
    return { header: header, body: body };
  }

  async function downloadCatalogCsv(btn, container) {
    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Preparing CSV…';
    setStatus(container, '<div class="csv-status__ok">Loading full catalog…</div>');

    try {
      var products = await fetchCatalog();

      // If we ended up on the products.json fallback, every UPC will be
      // empty — fetch /products/<handle>.js per product to fill in barcodes.
      var missingUpc = products.filter(function(p){ return p && p.handle && !p.upc; });
      if (missingUpc.length) {
        setStatus(container, '<div class="csv-status__ok">Fetching barcodes for ' + missingUpc.length + ' product(s)…</div>');
        await enrichBarcodesFromHandles(missingUpc, {
          concurrency: 8,
          onProgress: function(done, total){
            btn.textContent = 'Fetching barcodes ' + done + '/' + total + '…';
          }
        });
        writeCatalogCache(products);
      }

      var built = buildCsv(products);

      if (!built.body.length) {
        setStatus(container, '<div class="csv-status__warn">No products with an ID were found in the store.</div>');
        return;
      }

      var csv = [built.header].concat(built.body).join('\r\n');
      triggerDownload('destination-knitting-catalog.csv', csv);

      var totalWithUpc = products.filter(function(p){ return p.upc; }).length;
      setStatus(container, '<div class="csv-status__ok">&#x2713; Downloaded <strong>' + built.body.length + '</strong> products (' + totalWithUpc + ' with UPC). Add quantities in the <code>qty</code> column and re-upload the file.</div>');
    } catch(e) {
      setStatus(container, '<div class="csv-status__err"><strong>Could not build CSV:</strong> ' + escapeHtml(e && e.message ? e.message : String(e)) + '</div>');
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  }

  // Picks N random products from the catalog (preferring those with a UPC
  // and that are available) so the sample CSV is a realistic example of
  // what the store actually sells.
  function pickSampleProducts(products, n) {
    var available = products.filter(function(p){ return p && p.variantId && p.available !== false; });
    if (!available.length) available = products.filter(function(p){ return p && p.variantId; });

    var withUpc    = available.filter(function(p){ return p.upc; });
    var withoutUpc = available.filter(function(p){ return !p.upc; });

    function shuffle(arr) {
      var a = arr.slice();
      for (var i = a.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
      }
      return a;
    }

    var picked = shuffle(withUpc).slice(0, n);
    if (picked.length < n) picked = picked.concat(shuffle(withoutUpc).slice(0, n - picked.length));
    return picked;
  }

  async function downloadSampleCsv(btn, container) {
    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Preparing sample…';
    setStatus(container, '<div class="csv-status__ok">Building sample CSV…</div>');

    try {
      var products = await fetchCatalog();
      var sample = pickSampleProducts(products, 15);

      if (!sample.length) {
        setStatus(container, '<div class="csv-status__warn">No products were found in the store to build a sample.</div>');
        return;
      }

      // If the picked products don't have UPCs (template view unavailable),
      // enrich just these 15 from /products/<handle>.js — fast and accurate.
      var missingUpc = sample.filter(function(p){ return p && p.handle && !p.upc; });
      if (missingUpc.length) {
        setStatus(container, '<div class="csv-status__ok">Fetching barcodes for ' + missingUpc.length + ' product(s)…</div>');
        await enrichBarcodesFromHandles(missingUpc, { concurrency: 5 });
      }

      var built = buildCsv(sample);
      var csv = [built.header].concat(built.body).join('\r\n');
      triggerDownload('destination-knitting-sample.csv', csv);

      var withUpc = sample.filter(function(p){ return p.upc; }).length;
      var msg = '<div class="csv-status__ok">&#x2713; Downloaded a sample with <strong>' + built.body.length + '</strong> product(s) (' + withUpc + ' with UPC). Edit the <code>qty</code> column and re-upload to test.</div>';
      setStatus(container, msg);
    } catch(e) {
      setStatus(container, '<div class="csv-status__err"><strong>Could not build sample CSV:</strong> ' + escapeHtml(e && e.message ? e.message : String(e)) + '</div>');
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Empty cart
  // ─────────────────────────────────────────────────────────────
  async function emptyCart(btn) {
    if (!window.confirm('Remove all items from your cart?')) return;
    var originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Clearing…';
    try {
      var r = await fetchJsonOrText(root + 'cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!r.ok) throw new Error('Cart clear failed (HTTP ' + r.status + ')');
      await refreshCartSections();
    } catch(e) {
      btn.disabled = false;
      btn.textContent = originalLabel;
      alert(e && e.message ? e.message : 'Could not clear cart');
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Init — event delegation on document so freshly re-rendered
  // uploaders (after a cart refresh / empty-cart) always work without
  // needing per-element re-binding. This is what fixes the
  // "empty cart → Upload did nothing the first time" bug.
  // ─────────────────────────────────────────────────────────────
  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;

    var pickBtn = closest(t, '[data-csv-pick]');
    if (pickBtn) {
      var pc = closest(pickBtn, '[data-csv-uploader]');
      var input = pc && pc.querySelector('.csv-file-input');
      if (input) input.click();
      return;
    }

    var downloadBtn = closest(t, '[data-csv-download]');
    if (downloadBtn) {
      downloadCatalogCsv(downloadBtn, closest(downloadBtn, '[data-csv-uploader]'));
      return;
    }

    var sampleBtn = closest(t, '[data-csv-sample]');
    if (sampleBtn) {
      downloadSampleCsv(sampleBtn, closest(sampleBtn, '[data-csv-uploader]'));
      return;
    }

    var refreshBtn = closest(t, '[data-csv-refresh]');
    if (refreshBtn) {
      refreshCatalog(refreshBtn, closest(refreshBtn, '[data-csv-uploader]'));
      return;
    }

    var emptyBtn = closest(t, '[data-cart-empty]');
    if (emptyBtn) {
      emptyCart(emptyBtn);
      return;
    }

    if (closest(t, '[data-csv-report-continue]')) {
      continueAfterReport();
      return;
    }

    if (closest(t, '[data-csv-report-skip]')) {
      skipAfterReport();
      return;
    }

    if (closest(t, '[data-csv-report-close]')) {
      dismissReport();
      return;
    }

    if (closest(t, '[data-csv-report-download]')) {
      downloadProblemRows();
      return;
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.classList || !input.classList.contains('csv-file-input')) return;
    var container = closest(input, '[data-csv-uploader]');
    if (!container) return;
    var file = input.files && input.files[0];
    var filenameEl = container.querySelector('[data-csv-filename]');
    if (filenameEl) filenameEl.textContent = file ? file.name : 'No file selected';
    if (!file) return;
    handleFile(container, file).catch(function (err) {
      setStatus(container,
        '<div class="csv-status__err"><strong>Error:</strong> ' + escapeHtml(err && err.message ? err.message : err) + '</div>'
      );
    });
  });

  // After a bulk CSV add the page reloads; reopen the cart drawer on the fresh
  // load so the shopper immediately sees the newly-built cart. Retries briefly
  // in case the <cart-drawer> custom element hasn't upgraded yet.
  function reopenCartAfterReload() {
    var flag = null;
    try { flag = sessionStorage.getItem('dk_open_cart_after_reload'); } catch (e) {}
    if (flag !== '1') return;
    try { sessionStorage.removeItem('dk_open_cart_after_reload'); } catch (e) {}
    var attempts = 0;
    (function tryOpen() {
      var d = document.querySelector('cart-drawer');
      if (d && typeof d.open === 'function') { d.open(); return; }
      if (attempts++ < 20) setTimeout(tryOpen, 150);
    })();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reopenCartAfterReload);
  } else {
    reopenCartAfterReload();
  }
})();
